import {
  SelectField,
  TextField,
} from "@via/pages/projects/metadata/configuration/data-import/components/fields";
import KeyPanel from "@via/pages/projects/metadata/configuration/data-import/components/key-panel";
import {
  errorMessage,
  useCreateImportLogin,
  useDeleteImportLogin,
  useGenerateImportLoginKey,
  useTestStoredLogin,
  useUpdateImportLogin,
} from "@via/pages/projects/metadata/configuration/data-import/data-import.api.tsx";
import Modal from "@via/components/modal";
import {
  boundConfigsCount,
  credentialErrorMessage,
} from "@via/pages/projects/metadata/configuration/data-import/utils/form";
import type {
  VmetaImportLogin,
  VmetaImportLoginCreateBody,
  VmetaImportPlatform,
  VmetaImportTestLoginResult,
} from "common";
import { FC, useState } from "react";

const buttonClass =
  "rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50";

const LoginDrawerForm: FC<{
  login: VmetaImportLogin | null;
  platform: VmetaImportPlatform;
  organizations: { id: string; name: string }[];
  onClose: () => void;
}> = ({ login, platform, organizations, onClose }) => {
  const [current, setCurrent] = useState<VmetaImportLogin | null>(login);
  const [name, setName] = useState(login?.name ?? "");
  const [loginUrl, setLoginUrl] = useState(login?.loginUrl ?? "");
  const [apiOrigin, setApiOrigin] = useState(login?.apiOrigin ?? "");
  const [clientId, setClientId] = useState(login?.clientId ?? "");
  const [serviceAccount, setServiceAccount] = useState(
    login?.serviceAccount ?? ""
  );
  const [password, setPassword] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [refusal, setRefusal] = useState<string | null>(null);
  const [confirmRewrite, setConfirmRewrite] = useState<number | null>(null);
  const [loginResult, setLoginResult] =
    useState<VmetaImportTestLoginResult | null>(null);
  const { mutateAsync: create, isPending: isCreating } = useCreateImportLogin();
  const { mutateAsync: update, isPending: isUpdating } = useUpdateImportLogin();
  const { mutateAsync: remove } = useDeleteImportLogin();
  const { mutateAsync: generateKey } = useGenerateImportLoginKey();
  const { mutateAsync: testLogin, isPending: isTesting } = useTestStoredLogin();

  const identity =
    platform === "freezerpro" ? { clientId } : { serviceAccount };
  const onSave = async (rewriteBoundConfigs = false) => {
    setRefusal(null);
    try {
      if (current) {
        setCurrent(
          await update({
            id: current.id,
            body: {
              version: current.version,
              name,
              loginUrl,
              apiOrigin,
              ...identity,
              ...(password ? { password } : {}),
              ...(rewriteBoundConfigs ? { rewriteBoundConfigs: true } : {}),
            },
          })
        );
      } else {
        const body: VmetaImportLoginCreateBody = {
          name,
          platform,
          loginUrl,
          apiOrigin,
          ...identity,
          ...(password ? { password } : {}),
          ...(organizationId ? { organizationId } : {}),
        };
        setCurrent(await create(body));
      }
      setPassword("");
      setConfirmRewrite(null);
    } catch (err) {
      const count = boundConfigsCount(err);
      if (count !== null) {
        setConfirmRewrite(count);
        return;
      }
      setConfirmRewrite(null);
      setRefusal(
        credentialErrorMessage(
          err,
          "The credential could not be saved.",
          errorMessage
        )
      );
    }
  };

  const onTest = async () => {
    if (!current) return;
    setLoginResult(
      await testLogin({ id: current.id }).catch(err => ({
        ok: false as const,
        reason: errorMessage(err, "The login test could not run."),
      }))
    );
  };

  const onDelete = async () => {
    if (!current) return;
    try {
      await remove({ id: current.id });
      onClose();
    } catch (err) {
      setRefusal(errorMessage(err, "The credential could not be deleted."));
    }
  };

  return (
    <div className="h-full space-y-6 overflow-y-auto p-4">
      <TextField id="login-name" label="Name" value={name} onChange={setName} />
      {!current && organizations.length > 0 && (
        <SelectField
          id="login-belongs-to"
          label="Belongs to"
          value={organizationId}
          onChange={setOrganizationId}
          options={[
            { value: "", label: "Personal" },
            ...organizations.map(o => ({ value: o.id, label: o.name })),
          ]}
        />
      )}
      <TextField
        id="login-url"
        label="Login URL"
        value={loginUrl}
        onChange={setLoginUrl}
        hint="The password or key is only ever sent here."
      />
      <TextField
        id="login-api-origin"
        label="API address"
        value={apiOrigin}
        onChange={setApiOrigin}
        hint="Imports using this credential may only call URLs on this address."
      />
      {platform === "freezerpro" ? (
        <>
          <TextField
            id="login-username"
            label="Username"
            value={clientId}
            onChange={setClientId}
          />
          <TextField
            id="login-password"
            label="Password"
            type="password"
            value={password}
            placeholder={current?.hasSecret ? "stored" : ""}
            hint={
              current?.hasSecret
                ? "Leave blank to keep the stored password. Changing an address needs it again."
                : "Stored encrypted and never shown again."
            }
            onChange={setPassword}
          />
        </>
      ) : (
        <TextField
          id="login-service-account"
          label="Service account"
          value={serviceAccount}
          onChange={setServiceAccount}
        />
      )}
      {current && (
        <p className="text-sm text-gray-700">
          Used by {current.usedBy} {current.usedBy === 1 ? "import" : "imports"}
          .
        </p>
      )}
      {platform === "mydatahelps" && (
        <KeyPanel
          loginId={current?.id ?? null}
          certificate={current?.certificate ?? null}
          keyExpiresAt={current?.keyExpiresAt ?? null}
          version={current?.version ?? 0}
          usedBy={current?.usedBy ?? 0}
          generate={(id, version) => generateKey({ id, version })}
          onGenerated={result =>
            current &&
            setCurrent({
              ...current,
              certificate: result.certificate,
              keyExpiresAt: result.keyExpiresAt,
              version: result.version,
              usedBy: result.usedBy,
              hasSecret: true,
            })
          }
        />
      )}
      <div className="space-y-2">
        <button
          type="button"
          className={buttonClass}
          disabled={!current || isTesting}
          onClick={onTest}>
          Test login
        </button>
        {loginResult?.ok === true && (
          <p role="status" className="text-sm text-green-700">
            Login OK
          </p>
        )}
        {loginResult?.ok === false && (
          <p role="alert" className="text-sm text-red-700">
            Login failed: {loginResult.reason}
          </p>
        )}
      </div>
      {refusal && (
        <p
          role="alert"
          className="rounded-md bg-red-50 p-3 text-sm text-red-700">
          {refusal}
        </p>
      )}
      <div className="flex gap-3">
        <button
          type="button"
          disabled={isCreating || isUpdating}
          onClick={() => onSave()}
          className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50">
          Save
        </button>
        <button type="button" className={buttonClass} onClick={onClose}>
          Close
        </button>
        {current && (
          <button
            type="button"
            onClick={onDelete}
            className="ml-auto rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-500">
            Delete this credential
          </button>
        )}
      </div>
      <Modal
        open={confirmRewrite !== null}
        setOpen={() => setConfirmRewrite(null)}
        title="Move bound imports to the new address"
        confirmText="Update configs"
        isConfirmLoading={isUpdating}
        onConfirm={async () => {
          // Stays open, showing the loading state, until the resend settles. onSave closes it on success
          // or on a refusal, and keeps it open with the new count if the server asks again.
          await onSave(true);
          return false;
        }}>
        <p className="text-sm text-gray-700">{`This will update ${confirmRewrite ?? 0} import configs`}</p>
      </Modal>
    </div>
  );
};

export default LoginDrawerForm;
